'use client'

import React, { useState, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import { clsx } from 'clsx'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ListingItem } from '@/app/listings/components/Main/ListingItem'
import { Listing } from '@/api'
import { useRouter } from 'next/navigation'
import 'swiper/css'
import './modal.css'

type LikeListModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  Listings: Listing[]
}

export const LikeListModal = ({
  open,
  setOpen,
  Listings,
}: LikeListModalProps) => {
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(Listings.length <= 4)
  const swiperRef = useRef<any>(null)
  const router = useRouter()

  const handleSlideChange = (swiper: any) => {
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  const goToPrev = () => {
    if (swiperRef.current && swiperRef.current.slidePrev) {
      swiperRef.current.slidePrev()
    }
  }

  const goToNext = () => {
    if (swiperRef.current && swiperRef.current.slideNext) {
      swiperRef.current.slideNext()
    }
  }

  const handleViewAll = () => {
    setOpen(false)
    router.push('/mypage/like')
  }

  if (Listings.length === 0) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-none max-w-[90%] max-h-[90vh] lg:max-w-[55%] lg:max-h-[75vh] p-0 border-none shadow-lg fixed lg:top-8 lg:right-8 lg:translate-y-0 lg:-translate-x-28 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>いいね一覧</DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-6 min-h-[400px] flex items-center justify-center">
            <p className="text-gray-500">いいねした商品がありません</p>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white rounded-none max-w-full max-h-full lg:max-w-[55%] lg:max-h-[75vh] p-0 border-none shadow-lg fixed lg:top-8 lg:right-8 lg:translate-y-0 lg:-translate-x-28 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="text-xl font-medium">いいね一覧</DialogTitle>
        </DialogHeader>

        {/* コンテンツ部分 */}
        <div className="p-6 overflow-hidden">
          {/* PC表示: Swiper */}
          <div className="relative hidden lg:block">
            <Swiper
              modules={[Navigation]}
              slidesPerView={4}
              spaceBetween={24}
              loop={false}
              className="select-none"
              onSlideChange={handleSlideChange}
              onInit={handleSlideChange}
              onSwiper={(swiper) => {
                swiperRef.current = swiper
              }}
            >
              {Listings.map((listing) => (
                <SwiperSlide key={listing.id}>
                  <div className="h-full">
                    <ListingItem
                      uuid={listing.uuid || ''}
                      title={listing.product?.productMaster?.name || ''}
                      listingPrice={listing.price || 0}
                      image={listing.images?.[0]}
                      images={listing.images || undefined}
                      size={listing.size || undefined}
                      condition={listing.condition || undefined}
                      color={listing.product?.color || undefined}
                      sex={listing.product?.productMaster?.sex}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {!isBeginning && (
              <button
                type="button"
                title="goToPrev"
                onClick={goToPrev}
                className={clsx(
                  'absolute top-1/2 -translate-y-1/2 left-[-20px] z-10 w-10 h-10 cursor-pointer flex items-center justify-center bg-white border border-gray-300 rounded-full shadow-md hover:shadow-lg transition-shadow',
                  'before:content-[""] before:w-2.5 before:h-2.5 before:border-l-[2.5px] before:border-b-[2.5px] before:border-gray-600 before:rotate-45 before:translate-x-[1px]'
                )}
              ></button>
            )}
            {!isEnd && (
              <button
                type="button"
                title="goToNext"
                onClick={goToNext}
                className={clsx(
                  'absolute top-1/2 -translate-y-1/2 right-[-20px] z-10 w-10 h-10 cursor-pointer flex items-center justify-center bg-white border border-gray-300 rounded-full shadow-md hover:shadow-lg transition-shadow',
                  'before:content-[""] before:w-2.5 before:h-2.5 before:border-r-[2.5px] before:border-t-[2.5px] before:border-gray-600 before:rotate-45 before:-translate-x-[1px]'
                )}
              ></button>
            )}
          </div>

          {/* モバイル表示: 縦並びリスト */}
          <div className="lg:hidden">
            <div className="space-y-4">
              {Listings.slice(0, 4).map((listing) => (
                <div key={listing.id} className="w-full">
                  <ListingItem
                    uuid={listing.uuid || ''}
                    title={listing.product?.productMaster?.name || ''}
                    listingPrice={listing.price || 0}
                    image={listing.images?.[0]}
                    images={listing.images || undefined}
                    size={listing.size || undefined}
                    condition={listing.condition || undefined}
                    color={listing.product?.color || undefined}
                    sex={listing.product?.productMaster?.sex}
                    horizontal={true}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* すべて見るボタン */}
          <div className="mt-4 flex justify-center lg:justify-end">
            <button
              type="button"
              title="handleViewAll"
              onClick={handleViewAll}
              className="w-full lg:w-auto px-8 py-3 lg:py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-center"
            >
              すべて見る | →
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
